import {
  ExternalSourceSystem,
  ModernizedCourseContentSectionType,
  ModernizedQualityIndicator,
  ModernizedScoreCategory,
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  type GetModernizedCourseQuery,
} from "@axio/graphql/generated/graphql";

import type {
  ActivityDetail,
  ActivityRecommendationSource,
  CourseEvaluatorReviewCategory,
  CourseEvaluatorReviewDocumentIssue,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemKind,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
  CourseEvaluatorReviewItemType,
  PublishDocumentUpdate,
} from "./types/reviewTypes";
import { createActivityAttachment } from "./utils/attachments";

type ReviewCourse = NonNullable<GetModernizedCourseQuery["getModernizedCourse"]>;
type ReviewModule = ReviewCourse["modules"][number];
type ReviewAssignment = ReviewModule["assignments"][number];
type ReviewAssessment = ReviewModule["assessments"][number];
type ReviewProject = ReviewModule["projects"][number];
type ReviewOtherActivity = ReviewModule["otherActivities"][number];
type ReviewIleRecommendation = ReviewModule["ileRecommendations"][number];
type ReviewContentSection = ReviewCourse["contentSections"][number];
type ReviewRecommendation = {
  id?: string | null;
  recommendation?: string | null;
  rationale?: string | null;
  sourceType?: ActivityRecommendationSource["sourceType"] | null;
};

type ReviewIleObjective = NonNullable<ReviewIleRecommendation["aiTutor"]>["objectives"][number];

export type ReviewDataBundle = {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
};

const SCORE_CATEGORY_LABELS: Record<ModernizedScoreCategory, string> = {
  [ModernizedScoreCategory.CourseDesign]: "Course Design",
  [ModernizedScoreCategory.Assessment]: "Assessments",
  [ModernizedScoreCategory.Engagement]: "Engagement",
  [ModernizedScoreCategory.Accessibility]: "Accessibility",
};

const SOURCE_LABELS: Record<ExternalSourceSystem, string> = {
  [ExternalSourceSystem.Brightspace]: "Brightspace",
  [ExternalSourceSystem.Canvas]: "Canvas",
  [ExternalSourceSystem.Imscc]: "IMSCC",
  [ExternalSourceSystem.Pdf]: "PDF",
  [ExternalSourceSystem.Docx]: "DOCX",
};

const QUALITY_TO_EFFECTIVENESS: Record<ModernizedQualityIndicator, CourseEvaluatorReviewEffectiveness> = {
  [ModernizedQualityIndicator.Excellent]: "excellent",
  [ModernizedQualityIndicator.Good]: "good",
  [ModernizedQualityIndicator.Fair]: "fair",
  [ModernizedQualityIndicator.NeedsRevision]: "poor",
};

const CONTENT_SECTION_TITLES: Record<ModernizedCourseContentSectionType, string> = {
  [ModernizedCourseContentSectionType.LearningObjectives]: "Learning Objectives",
  [ModernizedCourseContentSectionType.CourseMaterialsList]: "Course Materials List",
  [ModernizedCourseContentSectionType.InstructorAvailability]: "Instructor Availability",
  [ModernizedCourseContentSectionType.Other]: "Other",
};

function mapActivityTypeToItemType(activityType: string | null | undefined): CourseEvaluatorReviewItemType {
  switch (activityType) {
    case "moduleDescription":
      return "moduleDescription";
    case "discussion":
      return "discussion";
    case "checklist":
      return "checklist";
    case "externalLink":
      return "externalLink";
    case "quiz":
      return "quiz";
    case "file":
      return "file";
    case "html-content":
      return "htmlContent";
    case "webpage":
      return "webpage";
    case "link":
      return "link";
    case "ile":
      return "ile";
    default:
      return "moduleDescription";
  }
}

function mapQualityToEffectiveness(
  quality: ModernizedQualityIndicator | null | undefined
): CourseEvaluatorReviewEffectiveness | undefined {
  if (!quality) return undefined;
  return QUALITY_TO_EFFECTIVENESS[quality];
}

function getLastModified(
  editLogs: Array<{ created?: string | null; actor?: { name?: string | null; username?: string | null } | null }>
) {
  const latest = [...editLogs].sort((left, right) => {
    const leftValue = left.created ? new Date(left.created).getTime() : 0;
    const rightValue = right.created ? new Date(right.created).getTime() : 0;
    return rightValue - leftValue;
  })[0];

  if (!latest) {
    return { lastModifiedBy: undefined, lastModifiedAt: undefined };
  }

  return {
    lastModifiedBy: latest.actor?.name ?? latest.actor?.username ?? "System",
    lastModifiedAt: latest.created
      ? new Date(latest.created).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : undefined,
  };
}

function mapRecommendationSources(
  recommendations: ReadonlyArray<ReviewRecommendation>
): ActivityRecommendationSource[] {
  return recommendations.flatMap((recommendation, index) => {
    const recommendationText = recommendation.recommendation?.trim();
    const rationale = recommendation.rationale?.trim();

    if (!recommendationText && !rationale) {
      return [];
    }

    return [
      {
        id: recommendation.id ?? `recommendation-${index}`,
        recommendation: recommendationText || undefined,
        rationale: rationale || undefined,
        sourceType: recommendation.sourceType ?? undefined,
      },
    ];
  });
}

function getRecommendationSummary(recommendations: ReadonlyArray<ReviewRecommendation>) {
  const recommendationSources = mapRecommendationSources(recommendations);
  const primary = recommendationSources[0];

  return {
    feedback: primary?.rationale,
    recommendedDescription: undefined,
    recommendationSources,
  };
}

function getContentSectionFeedback(recommendations: ReadonlyArray<ReviewRecommendation>): string | undefined {
  const rationaleParagraphs = recommendations
    .map((recommendation) => recommendation.rationale?.trim())
    .filter((rationale): rationale is string => Boolean(rationale));

  if (rationaleParagraphs.length === 0) {
    return undefined;
  }

  return [...new Set(rationaleParagraphs)].join("\n\n");
}

function mapGeneratedAssetsToAttachments(
  generatedAssets?: ReadonlyArray<{ id: string; fileName: string; fileURL?: string | null }> | null
) {
  if (!generatedAssets?.length) {
    return undefined;
  }

  return generatedAssets.map((asset) =>
    createActivityAttachment({
      id: asset.id,
      fileName: asset.fileName,
      url: asset.fileURL,
    })
  );
}

function buildMeta({
  itemType,
  isNew,
  qualityIndicator,
  tag,
}: {
  itemType?: CourseEvaluatorReviewItemType;
  isNew?: boolean | null;
  qualityIndicator?: ModernizedQualityIndicator | null;
  tag?: CourseEvaluatorReviewSidebarTag;
}): CourseEvaluatorReviewSidebarMeta[] | undefined {
  const meta: CourseEvaluatorReviewSidebarMeta[] = [];

  if (itemType && itemType !== "moduleDescription") {
    meta.push({ kind: "itemType", itemType });
  }

  if (tag) {
    meta.push({ kind: "tag", tag });
  }

  if (isNew) {
    meta.push({ kind: "tag", tag: "new" });
  }

  const effectiveness = mapQualityToEffectiveness(qualityIndicator);
  if (effectiveness) {
    meta.push({ kind: "effectiveness", effectiveness });
  }

  return meta.length > 0 ? meta : undefined;
}

function toCategoryBreakdown(course: ReviewCourse): CourseEvaluatorReviewCategory[] {
  return course.categoryScores.map((score) => ({
    name: SCORE_CATEGORY_LABELS[score.category] ?? score.category,
    score: score.score ?? 0,
    maxScore: score.maxScore ?? 0,
  }));
}

function toDocumentIssues(course: ReviewCourse): CourseEvaluatorReviewDocumentIssue[] {
  return course.inaccessibleDocuments.map((doc) => ({
    id: doc.id,
    title: doc.title ?? "Unknown document",
    message: doc.errorReason ?? "Document could not be accessed",
    documentIssueKind: doc.errorReason?.toLowerCase().includes("authentication") ? "permissionDenied" : "brokenLink",
  }));
}

function toDocumentUpdates(course: ReviewCourse): PublishDocumentUpdate[] {
  if (!course.syllabusFile) {
    return [];
  }

  return [
    {
      id: course.syllabusFile.id,
      title: course.syllabusFile.fileName,
      reason: "Add this updated syllabus document to the LMS course.",
      externalUrl: null,
      downloadUrl: course.syllabusFile.fileURL,
    },
  ];
}

function addContentSectionDetail(details: Record<string, ActivityDetail>, section: ReviewContentSection): void {
  const lastModified = getLastModified(section.editLogs);

  details[section.id] = {
    id: section.id,
    currentTitle: "",
    currentDescription: section.valueText ?? section.valueHtml ?? "",
    recommendedTitle: CONTENT_SECTION_TITLES[section.type] ?? section.type,
    recommendedDescription: section.recommendedValue ?? "",
    activityType: "recommendation",
    feedback: getContentSectionFeedback(section.recommendations),
    recommendationSources: undefined,
    documentReference: "Course Overview module",
    attachments: mapGeneratedAssetsToAttachments(section.generatedAssets),
    ...lastModified,
  };
}

function addModuleDescriptionItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule
): void {
  const itemId = module.id;

  items.push({
    id: itemId,
    title: module.title ?? "Untitled Module",
    kind: "moduleDescription",
    entityType: ModernizedActivityEntityType.Module,
    status: module.status,
    externalVisibility: module.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "moduleDescription", isNew: module.isNew, qualityIndicator: module.qualityIndicator }),
  });

  const lastModified = getLastModified(module.editLogs ?? []);

  details[itemId] = {
    id: itemId,
    currentTitle: module.title ?? "",
    currentDescription: module.description ?? "",
    recommendedTitle: module.recommendedTitle ?? module.title ?? "",
    recommendedDescription: module.recommendedDescription ?? "",
    activityType: "moduleDescription",
    feedback: undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    attachments: mapGeneratedAssetsToAttachments(module.generatedAssets),
    ...lastModified,
  };
}

function addAssignmentItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  assignment: ReviewAssignment
): void {
  const itemType = mapActivityTypeToItemType(assignment.gradingType);
  const tag =
    assignment.gradingType === "file" ? "lecture" : assignment.gradingType === "link" ? "supplementary" : undefined;
  const recommendationSummary = getRecommendationSummary(assignment.recommendations);
  const lastModified = getLastModified(assignment.editLogs);

  items.push({
    id: assignment.id,
    title: assignment.title ?? "Untitled Activity",
    kind: "assignment",
    entityType: ModernizedActivityEntityType.Assignment,
    status: assignment.status,
    externalVisibility: assignment.externalVisibility ?? undefined,
    meta: buildMeta({ itemType, isNew: assignment.isNew, qualityIndicator: assignment.qualityIndicator, tag }),
  });

  details[assignment.id] = {
    id: assignment.id,
    currentTitle: assignment.title ?? "",
    currentDescription: assignment.descriptionText ?? assignment.descriptionHtml ?? "",
    recommendedTitle: assignment.recommendedTitle ?? assignment.title ?? "",
    recommendedDescription:
      assignment.recommendedDescription ??
      recommendationSummary.recommendedDescription ??
      assignment.descriptionText ??
      "",
    activityType: assignment.gradingType ?? undefined,
    qualityLabel: assignment.qualityIndicator ?? undefined,
    pointsPossible: assignment.pointsPossible ?? undefined,
    recommendedPointsPossible: assignment.recommendedPointsPossible ?? undefined,
    dueDate: assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: assignment.recommendedDueDate
      ? new Date(assignment.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: assignment.recommendedDueDateCleared ?? undefined,
    url: assignment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${assignment.title ?? "Activity"}`,
    contentCategory: tag,
    attachments: mapGeneratedAssetsToAttachments(assignment.generatedAssets),
    ...lastModified,
  };
}

function addAssessmentItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  assessment: ReviewAssessment
): void {
  const recommendationSummary = getRecommendationSummary(assessment.recommendations);
  const lastModified = getLastModified(assessment.editLogs);

  items.push({
    id: assessment.id,
    title: assessment.title ?? "Untitled Assessment",
    kind: "assignment",
    entityType: ModernizedActivityEntityType.Assessment,
    status: assessment.status,
    externalVisibility: assessment.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "quiz", isNew: assessment.isNew, qualityIndicator: assessment.qualityIndicator }),
  });

  details[assessment.id] = {
    id: assessment.id,
    currentTitle: assessment.title ?? "",
    currentDescription: "",
    recommendedTitle: assessment.title ?? "",
    recommendedDescription: recommendationSummary.recommendedDescription ?? "",
    activityType: "quiz",
    qualityLabel: assessment.qualityIndicator ?? undefined,
    pointsPossible: assessment.pointsPossible ?? undefined,
    recommendedPointsPossible: assessment.recommendedPointsPossible ?? undefined,
    dueDate: assessment.dueDate ? new Date(assessment.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: assessment.recommendedDueDate
      ? new Date(assessment.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: assessment.recommendedDueDateCleared ?? undefined,
    url: assessment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${assessment.title ?? "Assessment"}`,
    attachments: mapGeneratedAssetsToAttachments(assessment.generatedAssets),
    ...lastModified,
  };
}

function addProjectItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  project: ReviewProject
): void {
  const recommendationSummary = getRecommendationSummary(project.recommendations);
  const lastModified = getLastModified(project.editLogs);

  items.push({
    id: project.id,
    title: project.title ?? "Untitled Project",
    kind: project.isNew ? "suggestedContent" : "assignment",
    entityType: ModernizedActivityEntityType.Project,
    status: project.status,
    externalVisibility: project.externalVisibility ?? undefined,
    meta: buildMeta({ itemType: "htmlContent", isNew: project.isNew, qualityIndicator: project.qualityIndicator }),
  });

  details[project.id] = {
    id: project.id,
    currentTitle: project.title ?? "",
    currentDescription: project.descriptionText ?? project.descriptionHtml ?? "",
    recommendedTitle: project.recommendedTitle ?? project.title ?? "",
    recommendedDescription: project.recommendedDescription ?? recommendationSummary.recommendedDescription ?? "",
    activityType: "html-content",
    qualityLabel: project.qualityIndicator ?? undefined,
    dueDate: project.dueDate ? new Date(project.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: project.recommendedDueDate
      ? new Date(project.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: project.recommendedDueDateCleared ?? undefined,
    url: project.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    documentReference: `Content > ${module.title ?? "Module"} > ${project.title ?? "Project"}`,
    attachments: mapGeneratedAssetsToAttachments(project.generatedAssets),
    ...lastModified,
  };
}

function addOtherActivityItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  activity: ReviewOtherActivity
): void {
  const recommendationSummary = getRecommendationSummary(activity.recommendations);
  const lastModified = getLastModified(activity.editLogs);
  // A "webpage" activity with no HTML body but a non-empty external URL was
  // misclassified during import (e.g. a Brightspace URL topic whose URL happened
  // to end in .html).  Treat it as a link so it renders in ExternalLinkPanel.
  const effectiveActivityType =
    activity.activityType === "webpage" && !activity.descriptionHtml && Boolean(activity.externalUrl)
      ? "link"
      : activity.activityType;
  const itemType = mapActivityTypeToItemType(effectiveActivityType);
  const isSuggested = effectiveActivityType === "ile" || Boolean(activity.isNew);
  const kind: CourseEvaluatorReviewItemKind = isSuggested ? "suggestedContent" : "assignment";
  const isWebpage = effectiveActivityType === "webpage";
  // For webpage activities the body is raw HTML — always prefer descriptionHtml.
  // For other types (quiz, discussion, etc.) use descriptionText where available.
  // Use || not ?? so an empty string falls through (Brightspace quiz descriptions
  // are often HTML-only with descriptionText stored as "").
  // If we only have descriptionHtml for a non-webpage, strip tags so the
  // plain-text diff view shows readable content instead of raw markup.
  const rawCurrentHtml = activity.descriptionHtml ?? "";
  const rawCurrentText = activity.descriptionText ?? "";
  const strippedHtml = rawCurrentHtml
    .replace(/<[^>]*>/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  const currentDescription = isWebpage ? rawCurrentHtml || rawCurrentText : rawCurrentText || strippedHtml;
  // For webpages, only use recommendedDescription if it contains HTML tags — meaning
  // the reviewer explicitly edited the HTML source.  The AI planner sets
  // recommendedDescription to plain text for webpages, which would strip all
  // embedded media/links if pushed as the file body.  Always fall back to the
  // original descriptionHtml so the file content is preserved by default.
  // For webpages, prefer any explicitly saved recommendedDescription (even plain
  // text) over the AI-generated one, which may strip embedded media/links.
  // We detect an explicit user edit by checking whether the saved value differs
  // from the current descriptionHtml — if they are identical the reviewer has
  // not changed anything and we fall back to descriptionHtml.
  const savedRec = activity.recommendedDescription;
  const isExplicitWebpageEdit = isWebpage && savedRec != null && savedRec !== activity.descriptionHtml;
  const webpageRec = isExplicitWebpageEdit ? savedRec : undefined;
  const nonWebpageRec = activity.recommendedDescription ?? recommendationSummary.recommendedDescription;
  const aiOrUserRecommendedDescription = isWebpage ? webpageRec : nonWebpageRec;
  const recommendedDescription = isWebpage
    ? (aiOrUserRecommendedDescription ?? activity.descriptionHtml ?? "")
    : (aiOrUserRecommendedDescription ?? "");

  items.push({
    id: activity.id,
    title: activity.title ?? module.title ?? "Untitled Activity",
    kind,
    entityType: ModernizedActivityEntityType.OtherActivity,
    status: activity.status,
    externalVisibility: activity.externalVisibility ?? undefined,
    meta: buildMeta({ itemType, isNew: activity.isNew, qualityIndicator: activity.qualityIndicator }),
  });

  details[activity.id] = {
    id: activity.id,
    currentTitle: activity.title ?? "",
    currentDescription,
    recommendedTitle: activity.title ?? "",
    recommendedDescription,
    activityType: activity.activityType ?? undefined,
    suggestedContentType: isSuggested ? (activity.activityType ?? undefined) : undefined,
    qualityLabel: activity.qualityIndicator ?? undefined,
    dueDate: activity.dueDate ? new Date(activity.dueDate).toLocaleDateString("en-US") : undefined,
    recommendedDueDate: activity.recommendedDueDate
      ? new Date(activity.recommendedDueDate).toLocaleDateString("en-US")
      : undefined,
    recommendedDueDateCleared: activity.recommendedDueDateCleared ?? undefined,
    url: activity.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    recommendationSources: recommendationSummary.recommendationSources,
    learningObjective: activity.activityType === "ile" ? (recommendationSummary.feedback ?? undefined) : undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    attachments: mapGeneratedAssetsToAttachments(activity.generatedAssets),
    ...lastModified,
  };
}

function addIleRecommendationItem(
  details: Record<string, ActivityDetail>,
  items: CourseEvaluatorReviewSidebarItem[],
  module: ReviewModule,
  ileRecommendation: ReviewIleRecommendation
): void {
  const recommendationSummary = getRecommendationSummary(ileRecommendation.recommendations);
  const lastModified = getLastModified(ileRecommendation.editLogs);
  const createdIleObjectives = ileRecommendation.aiTutor?.objectives
    .filter((objective): objective is ReviewIleObjective => Boolean(objective?.objective))
    .map((objective) => ({
      id: objective.id,
      objective: objective.objective ?? "",
      sequence: objective.sequence,
      evalCriteria: (objective.evalCriteria ?? []).filter((criteria): criteria is string => Boolean(criteria)),
    }));

  items.push({
    id: ileRecommendation.id,
    title: ileRecommendation.title ?? module.title ?? "Untitled Activity",
    kind: "suggestedContent",
    entityType: ModernizedActivityEntityType.IleRecommendation,
    status: ileRecommendation.status,
    meta: buildMeta({ itemType: "ile", isNew: true }),
  });

  details[ileRecommendation.id] = {
    id: ileRecommendation.id,
    currentTitle: ileRecommendation.title ?? "",
    currentDescription: ileRecommendation.description ?? "",
    recommendedTitle: ileRecommendation.title ?? "",
    recommendedDescription: recommendationSummary.recommendedDescription ?? "",
    activityType: "ile",
    suggestedContentType: "ile",
    feedback: recommendationSummary.feedback,
    learningObjective: recommendationSummary.feedback ?? undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    createdIleId: ileRecommendation.aiTutor?.id,
    createdIleTitle: ileRecommendation.aiTutor?.title,
    createdIleDescription: ileRecommendation.aiTutor?.description,
    createdIleContext: ileRecommendation.aiTutor?.context,
    createdIleDueDate: ileRecommendation.aiTutor?.dueDate,
    createdIleTargetAudience: ileRecommendation.aiTutor?.targetAudience,
    createdIleActivityState: ileRecommendation.aiTutor?.activityState,
    createdIleScheduledDate: ileRecommendation.aiTutor?.scheduledDate,
    createdIleObjectives,
    ...lastModified,
  };
}

function buildModuleGroups(
  course: ReviewCourse,
  details: Record<string, ActivityDetail>
): CourseEvaluatorReviewSidebarGroup[] {
  return course.modules.map((module) => {
    const items: CourseEvaluatorReviewSidebarItem[] = [];

    addModuleDescriptionItem(details, items, module);
    module.assignments.forEach((assignment) => addAssignmentItem(details, items, module, assignment));
    module.assessments.forEach((assessment) => addAssessmentItem(details, items, module, assessment));
    module.projects.forEach((project) => addProjectItem(details, items, module, project));
    module.otherActivities.forEach((activity) => addOtherActivityItem(details, items, module, activity));
    module.ileRecommendations.forEach((ile) => addIleRecommendationItem(details, items, module, ile));

    return {
      id: module.id,
      title: module.title ?? "Untitled Module",
      decided: 0,
      total: items.length,
      defaultExpanded: course.modules[0]?.id === module.id,
      externalUrl: module.externalUrl ?? undefined,
      items,
    };
  });
}

function buildCourseLevelGroup(
  course: ReviewCourse,
  details: Record<string, ActivityDetail>
): CourseEvaluatorReviewSidebarGroup {
  const items: CourseEvaluatorReviewSidebarItem[] = course.contentSections.map((section) => {
    addContentSectionDetail(details, section);
    return {
      id: section.id,
      title: CONTENT_SECTION_TITLES[section.type] ?? section.type,
      kind: "recommendation",
      entityType: ModernizedActivityEntityType.ContentSection,
      status: section.status,
      meta: section.isNew ? [{ kind: "tag", tag: "new" }] : undefined,
    };
  });

  return {
    id: "course-level-content",
    title: "Course-Level Content",
    decided: 0,
    total: items.length,
    defaultExpanded: true,
    items,
  };
}

export function mapModernizedCourseToReviewData(course: ReviewCourse): ReviewDataBundle {
  const activityDetails: Record<string, ActivityDetail> = {};
  const courseLevelGroup = buildCourseLevelGroup(course, activityDetails);
  const moduleGroups = buildModuleGroups(course, activityDetails);
  const sidebarGroups = [courseLevelGroup, ...moduleGroups];
  const totalCount = sidebarGroups.reduce((sum, group) => sum + group.total, 0);
  const allItems = sidebarGroups.flatMap((group) => group.items);
  const queuedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Queued).length;
  const skippedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Skipped).length;
  const publishedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Published).length;
  const reviewedCount = queuedCount + skippedCount + publishedCount;

  return {
    review: {
      courseId: course.id,
      courseName: course.title ?? "",
      sourceLabel: course.externalSource ? SOURCE_LABELS[course.externalSource] : "Unknown",
      courseCode: course.courseCode ?? "",
      termLabel: course.term ?? "",
      frameworkName: "Dallas College Online Teaching Framework 3.1",
      reviewStage: "full-course-review",
      effectiveness: course.qualityRating ? QUALITY_TO_EFFECTIVENESS[course.qualityRating] : "good",
      reviewedDateLabel: course.modules[0]?.lastModified
        ? new Date(course.modules[0].lastModified).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
      score: course.totalScore ?? 0,
      maxScore: course.totalMaxScore ?? 50,
      reviewedCount,
      totalCount,
      queuedCount,
      skippedCount,
      publishedCount,
      categoryBreakdown: toCategoryBreakdown(course),
      strengths: [...course.strengths],
      improvements: [...course.itemsToImprove],
      documentIssues: toDocumentIssues(course),
      documentUpdates: toDocumentUpdates(course),
      sidebarGroups,
    },
    activityDetails,
  };
}
